import type { ProductCatalog } from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import type { Site } from '@hotjar/site';
import type { CostSpecDTO } from '@hotjar/webapp/domains/billing';
import {
  getProductSkusByPlanId,
  getAnalyticsProductBySku,
  getFeedbackProductBySku,
} from '@hotjar/webapp/domains/common/product/utils';
import type { ChangeListItem } from './types';

interface GetChangesSummary {
  productCatalog?: ProductCatalog;
  costSpecs?: CostSpecDTO[];
  sites?: Site[];
}

export function getChangesSummary({
  productCatalog,
  costSpecs,
  sites,
}: GetChangesSummary): ChangeListItem[] {
  if (!productCatalog || !costSpecs || !sites) {
    return [];
  }

  const changedCostSpecs = costSpecs.filter((costSpec) => {
    return sites.find((site) => site.id === costSpec.site_id && site.planId !== costSpec.plan_id);
  });

  return changedCostSpecs.map((changedCostSpec) => {
    const changedSite = sites.find((site) => site.id === changedCostSpec.site_id);
    const changesSummary: ChangeListItem = {
      siteLink: changedSite?.url ?? '',
      newPlans: [],
      currentPlans: [],
    };

    if (!changedSite) {
      return changesSummary;
    }

    const [oldAnalyticsSku, oldFeedbackSku] = getProductSkusByPlanId(changedSite.planId);
    const [newAnalyticsSku, newFeedbackSku] = getProductSkusByPlanId(changedCostSpec.plan_id);

    if (oldFeedbackSku !== newFeedbackSku) {
      const oldFeedbackProduct = getFeedbackProductBySku(oldFeedbackSku, productCatalog);
      const newFeedbackProduct = getFeedbackProductBySku(newFeedbackSku, productCatalog);
      changesSummary.currentPlans.push({
        productType: ProductType.Feedback,
        type: oldFeedbackProduct.tier,
        limit: oldFeedbackProduct.monthlyResponses,
      });
      changesSummary.newPlans.push({
        productType: ProductType.Feedback,
        type: newFeedbackProduct.tier,
        limit: newFeedbackProduct.monthlyResponses,
      });
    }

    if (oldAnalyticsSku !== newAnalyticsSku) {
      const oldAnalyticsProduct = getAnalyticsProductBySku(oldAnalyticsSku, productCatalog);
      const newAnalyticsProduct = getAnalyticsProductBySku(newAnalyticsSku, productCatalog);
      changesSummary.currentPlans.push({
        productType: ProductType.Analytics,
        type: oldAnalyticsProduct.tier,
        limit: oldAnalyticsProduct.dailyCcSessions,
      });
      changesSummary.newPlans.push({
        productType: ProductType.Analytics,
        type: newAnalyticsProduct.tier,
        limit: newAnalyticsProduct.dailyCcSessions,
      });
    }

    return changesSummary;
  });
}
